// frontend/src/utils/week.js
//
// Week boundaries for a UK business date.
//
// Every "date" in this system is a calendar day at a forecourt, not an instant
// in time. The helpers dotted through the components computed the week with
// LOCAL date arithmetic and then serialised with toISOString(), which is UTC —
// and during British Summer Time local midnight is 23:00 UTC the day before.
// So between 00:00 and 01:00 BST, mondayOf() returned the SUNDAY, and every
// weekly page opened on a night shift showed the previous week.
//
// These format from local calendar parts, so the arithmetic and the string
// agree. Outside that midnight hour the results are identical to the old
// helpers, so swapping them in changes nothing else.

/** "YYYY-MM-DD" from the local calendar day — never via toISOString(). */
export function ymd(value) {
  if (!value) return '';

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');

  return `${y}-${m}-${d}`;
}

/** Parse a date-ish value to local midnight, so day maths cannot drift. */
function atLocalMidnight(value) {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [y, m, d] = value.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  const date = value instanceof Date ? new Date(value) : new Date(value || Date.now());
  if (Number.isNaN(date.getTime())) return null;

  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Monday of the week containing `value`, as "YYYY-MM-DD". */
export function mondayOf(value = new Date()) {
  const date = atLocalMidnight(value);
  if (!date) return '';

  const day = date.getDay(); // 0 = Sunday
  date.setDate(date.getDate() + (day === 0 ? -6 : 1 - day));

  return ymd(date);
}

/** Sunday of the week containing `value`, as "YYYY-MM-DD". */
export function sundayOf(value = new Date()) {
  const monday = atLocalMidnight(mondayOf(value));
  if (!monday) return '';

  monday.setDate(monday.getDate() + 6);
  return ymd(monday);
}

/** Shift a date-ish value by whole days, returning "YYYY-MM-DD". */
export function addDays(value, days) {
  const date = atLocalMidnight(value);
  if (!date) return '';

  date.setDate(date.getDate() + Number(days || 0));
  return ymd(date);
}

/**
 * The trading day a site is working on, as "YYYY-MM-DD".
 *
 * Sites reconcile and file sales a day behind: on Tuesday morning they are
 * doing Monday's banking and uploading Monday's sales, and both have to be
 * filed against Monday. The screens opened on today and on the Monday of
 * the current week respectively, so the first thing anybody did each
 * morning was change the date - and a morning where somebody forgot files
 * the banking against the wrong day and skips every dated row in the
 * spreadsheet.
 *
 * Local calendar arithmetic, like everything else here: at 00:30 BST
 * "yesterday" is the day before the local date, not the day before the UTC
 * one.
 */
export function tradingDay(value = new Date()) {
  return addDays(value, -1);
}

/** The seven "YYYY-MM-DD" keys of the week containing `value`. */
export function weekDays(value = new Date()) {
  const monday = mondayOf(value);
  if (!monday) return [];

  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

/**
 * The business day a record belongs to, as "YYYY-MM-DD".
 *
 * Records reach the front end with the date under one of half a dozen names
 * depending on which collection and which era they came from, and an existing
 * YYYY-MM-DD string is returned untouched so a timezone conversion can never
 * move a record into the neighbouring day.
 *
 * Lifted out of SmartOperationsDashboard when a second component needed it.
 * Duplicating this precedence is how a record ends up counted on one screen and
 * missing on another.
 */
export function recordDateKey(row) {
  const raw =
    row?.businessDate ??
    row?.salesDate ??
    row?.reportDate ??
    row?.date ??
    row?.day ??
    row?.createdAt;

  if (!raw) return '';

  const isoMatch = String(raw).match(/^\d{4}-\d{2}-\d{2}/);
  if (isoMatch) return isoMatch[0];

  return ymd(raw);
}
